<template>
  <div v-click-outside="closeSelect" class="budget-actions__parent-element">
    <div
      class="sf-form-select-wrapper budget-actions__sf-form-select-wrapper"
      :style="wrapperStyles"
    >
      <div :class="formSelectClass" @click="toggleSelect">
        <div
          v-tooltip="tooltipValue"
          v-dompurify-html="actionSelectLabel"
          :class="actionSelectClass"
        />
      </div>
    </div>

    <div v-if="open" class="budget-actions__actions-card-wrapper">
      <div class="budget-actions__actions-card">
        <SfFlash
          v-if="errors.serverError"
          type="alert"
          :message="I18n.t('budget_set.actions.error')"
          wrapper-classes="budget-actions__budget-actions-error"
        />

        <ul class="budget-actions__actions-card-element">
          <BudgetManualAction
            v-for="manualAction in manualActions"
            :key="manualAction.name"
            :manual-action-type="manualAction.name"
            :error="errors[manualAction.name]"
            :default-value="
              defaulValueForManualAction({ name: manualAction.name })
            "
            :label="labelText(manualAction.name)"
            :show-percentage="
              'showPercentage' in manualAction && manualAction.showPercentage
            "
            :icon-class="manualAction.iconClass"
            :previous-action="previousAction"
            :tooltip="tooltipText(manualAction.name)"
            :processing="manualAction.name === processingAction"
            @submit="submitAction"
          />
        </ul>

        <div
          class="
            budget-actions__actions-card-element
            budget-actions__actions-card-bordered-element
            budget-actions__delete-data
          "
        >
          <SfButtonDs variant="secondary" size="sm" @click="deleteData">
            {{ I18n.t('budget_set.actions.delete') }}
          </SfButtonDs>
        </div>
      </div>
    </div>

    <BudgetDeleteRowModal
      v-if="isDeleteModalOpen"
      row-type="all"
      :set-action="setAction"
      @close="closeDeleteModal"
    />
  </div>
</template>
<script setup lang="ts">
import type {
  Action,
  AvailableActionsMap,
  BudgetActionPaths,
  BudgetEntry,
  ManualActions,
  ManualActionAvailableOn,
  ManualActionsMap,
  ManualActionNames,
  PredictionValues,
} from '../types'

import { isManualAction, isSeasonalityManualAction } from '../predicates'
import type { Ref } from 'vue'
import { storeToRefs } from 'pinia'
import {
  computed,
  onBeforeMount,
  onMounted,
  onUnmounted,
  ref,
  reactive,
} from 'vue'

import { actions } from '../constants'
import {
  getUrlOptionsFromBudgetTable,
  renderBudgetEntries,
} from '../handlers/budget-table-api'

import BudgetManualAction from './budget-manual-action.vue'
import BudgetDeleteRowModal from './budget-delete-row-modal.vue'
import { usePredictionsStore } from '@app/application/budget/stores/predictions'
import { useBudgetActionsStore } from '@app/application/budget/stores/budget-actions'
import SfFlash from '@app/shared/vue/components/sf-flash.vue'
import { SfButtonDs } from '@silverfin/ui-components'
import Sfetch from '@app/utils/sfetch'

defineOptions({
  name: 'BudgetActionsSelect',
})

interface Props {
  defaultValues: {
    copyValue: string
    seasonality: string
    spreadValue: string
    divider: string
  }
  rowType: ManualActionAvailableOn
  rowElementId: string
  seededLastAction?: Action
  tableStructure: Record<number, string[]>
}

const props = withDefaults(defineProps<Props>(), {
  seededLastAction: () => ({ type: null, value: null }),
})
const defaultValues = reactive(props.defaultValues)
const processingAction: Ref<string | null> = ref(null)
const open = ref(false)
const errors: Ref<Record<string, boolean>> = ref(setupErrors())
const observers: Ref<Record<string, MutationObserver>> = ref({})
const isDeleteModalOpen = ref(false)
const budgetActionsStore = useBudgetActionsStore()
const predictionsStore = usePredictionsStore()
const { initialized, lastActionRowIds, previousActions } =
  storeToRefs(budgetActionsStore)

const { initialize, setPreviousAction } = budgetActionsStore
const {
  getAllAvailablePredictionTypes,
  getPredictionsPerAccount,
  predictionTypesNeedingReviewPerAccount,
} = storeToRefs(predictionsStore)
const { setAccountAsReviewed } = predictionsStore
const I18n = window.I18n

function tooltipText(automatedActionName: string): string {
  return I18n.t(`budget_set.actions.tooltips.${automatedActionName}`)
}
function labelText(automatedActionName: string): string {
  return I18n.t(`budget_set.actions.${automatedActionName}`)
}

const predictions = computed(() => {
  if (props.rowType === 'account') {
    return getPredictionsPerAccount.value[elementId.value]
  } else {
    return getAllAvailablePredictionTypes
  }
})

const reviewData = computed(() => {
  // Without predictions no need to review
  if (!predictions.value) {
    return false
  }

  const predictionTypesNeedingReview =
    predictionTypesNeedingReviewPerAccount.value[elementId.value]
  return !!predictionTypesNeedingReview
})

const needsReview = computed(() => {
  // It cannot need a review if it's not marked as review data
  if (!reviewData.value) {
    return false
  }

  // If the previous action type is in the types that need review for this account, mark it as such
  const predictionTypesNeedingReview =
    predictionTypesNeedingReviewPerAccount.value[elementId.value]
  const previousActionType = previousAction.value?.type

  return (
    predictionTypesNeedingReview &&
    predictionTypesNeedingReview.includes(previousActionType as string)
  )
})

const manualActions = computed(() =>
  Object.values(actions).reduce((acc, action) => {
    if (isManualAction(action)) {
      const availableOn = action.available_on as ManualActionAvailableOn[]

      if (availableOn.includes(props.rowType)) {
        acc.push(action)
      }
    }

    return acc
  }, [] as ManualActions[])
)

const previousAction = computed(() => {
  if (previousActions.value[elementId.value]) {
    return previousActions.value[elementId.value].action
  }

  return { value: null, type: null }
})

const wrapperStyles = computed(() => {
  return {
    // In some languages the actions label is very large, leading to visual issues
    width: 90 + I18n.t('budget_set.actions.label').length * 2 + 'px',
  }
})

const defaultValue = computed(() => {
  return {
    spread: `${defaultValues.spreadValue}/${defaultValues.divider}`,
    copy: `${defaultValues.copyValue}/${defaultValues.divider}`,
    seasonality: defaultValues.seasonality,
  }
})

const tooltipValue = computed(() => {
  // Remove the icon from the full label
  const label = actionSelectLabel.value.replace(/(<[^>]*>)/gi, '')

  if (
    !open.value &&
    label.length > I18n.t('budget_set.actions.label').length &&
    previousAction.value
  ) {
    return label
  }

  return null
})

const elementId = computed(() => {
  return props.rowType === 'all' ? 'all' : props.rowElementId
})

const actionSelectClass = computed(() => {
  return {
    'budget-actions__select-text': true,
    'budget-actions__select-open': open.value,
    'sf-c-primary': lastActionRowIds.value.includes(elementId.value),
    'sf-c-danger-light-1': needsReview.value,
  }
})

const formSelectClass = computed(() => {
  return {
    'sf-form-select': true,
    'budget-actions__sf-form-select': true,
    'budget-actions__sf-form-select-open': open.value,
  }
})

const actionSelectLabel = computed(() => {
  const label = window.I18n.t('budget_set.actions.label')

  const action = previousAction.value

  if (action && action.type) {
    const icon = iconClassForAction(
      action.type as keyof typeof iconClassForAction
    )

    const actionLabel = action.value ? action.value : label

    return `<i class="${icon}"></i> ${actionLabel}`
  }

  if (!action && lastActionRowIds.value.includes(elementId.value)) {
    return `<i class="fa fa-pencil"></i> ${label}`
  }

  return label
})

onBeforeMount(() => {
  if (!initialized.value) {
    initialize({
      tableStructure: props.tableStructure,
      seededLastAction:
        props.seededLastAction?.type || props.seededLastAction?.value
          ? props.seededLastAction
          : null,
    })
  }
})

onMounted(() => {
  document
    .querySelectorAll(`[data-element-id='${props.rowElementId}'] input`)
    .forEach((cell) => {
      cell.addEventListener('input', removePreviousAction)
    })

  const config = { attributes: true, childList: true, subtree: true }
  const spreadValueElement = document.querySelector(
    `[data-element-id='${props.rowElementId}'] [data-behaviour='budget-ledger-values']`
  )

  if (spreadValueElement) {
    observers.value['spreadValue'] = new MutationObserver(updateSpreadValue)
    observers.value.spreadValue.observe(spreadValueElement, config)
  }

  const dividerElement = document.querySelector(
    `[data-behaviour='budget-divider']`
  )

  if (dividerElement) {
    observers.value['divider'] = new MutationObserver(updateDivider)
    observers.value.divider.observe(dividerElement, config)
  }
})

onUnmounted(() => {
  document
    .querySelectorAll(`[data-element-id='${props.rowElementId}'] input`)
    .forEach((cell) => {
      cell.removeEventListener('input', removePreviousAction)
    })

  Object.values(observers.value).map((observer) => observer.disconnect())
})

function setupErrors(): Record<string, boolean> {
  const settingUpErrors: Record<string, boolean> = { server: false }

  Object.values(actions).map((action) => {
    settingUpErrors[action.name] = false
  })

  return settingUpErrors
}

function closeSelect(): void {
  open.value = false
  errors.value = setupErrors()
}

function toggleSelect(): void {
  if (open.value) {
    closeSelect()
  } else {
    open.value = true
  }
}

function iconClassForAction(actionType: keyof typeof actions): string {
  if (needsReview.value) {
    return 'fa fa-solid fa-triangle-exclamation'
  } else if (actionType in actions) {
    return actions[actionType].iconClass
  } else {
    return 'fa fa-wand-magic-sparkles'
  }
}

interface ManualActionPayload {
  value: string
  actionType: keyof ManualActionsMap
}

type SubmitActionPayload = ManualActionPayload | never

const raiseBadActionError = (actionType: ManualActionNames): void => {
  errors.value[actionType] = true
}

function makePostRequestOnSubmit(
  actionType: ManualActionNames,
  value: string | Record<string, PredictionValues[]>
): void {
  processingAction.value = actionType

  const path = actions[actionType].path

  makeRequest(path, {
    method: 'POST',
    body: { type: props.rowType, elementId: props.rowElementId, value },
  })
}

const submitAction = (payload: SubmitActionPayload): void => {
  const { value, actionType } = payload
  const hasValue = !!value && value !== ''

  if (hasValue) {
    makePostRequestOnSubmit(actionType, value)
    setAction(value, actionType, {})
  } else {
    raiseBadActionError(actionType)
  }
}

function deleteData(): void {
  if (props.rowType === 'all') {
    isDeleteModalOpen.value = true
    return
  }

  setAction(null, null)

  makeRequest('delete_budget_set_budget_entries_path', {
    method: 'DELETE',
    body: {
      type: props.rowType,
      elementId: props.rowElementId,
    },
  })
}

function closeDeleteModal(): void {
  isDeleteModalOpen.value = false
}

function generateRequestUrl(path: BudgetActionPaths): string {
  const urlOptions = getUrlOptionsFromBudgetTable()

  const route = window.Silverfin.Routes[path]

  const url = route(
    urlOptions?.firm_id,
    urlOptions?.company_id,
    urlOptions?.budget_set_id,
    urlOptions?.budget_set_id,
    { ...urlOptions }
  )

  return url
}

async function makeRequest(
  path: BudgetActionPaths,
  options: {
    method: string
    body: {
      type: ManualActionAvailableOn
      elementId: string
      value?: Record<string, PredictionValues[]> | string
    }
  }
): Promise<void> {
  const url = generateRequestUrl(path)

  errors.value['serverError'] = false

  try {
    const budgetEntries = await Sfetch.json<BudgetEntry[]>(url, options)
    renderBudgetEntries(budgetEntries ?? [])
    processingAction.value = null
    closeSelect()
  } catch {
    processingAction.value = null
    errors.value['serverError'] = true
  }
}

function removePreviousAction(): void {
  if (needsReview.value) {
    setAccountAsReviewed(elementId.value)
  }

  setAction(null, null)
}

function updateSpreadValue(mutationList: MutationRecord[]): void {
  const mutation = mutationList.find(
    (mutation) => mutation.type === 'childList'
  )

  if (mutation) {
    defaultValues.spreadValue = (mutation.addedNodes[0] as Text).data // Regular nodes don't have data, only text nodes
  }
}

function updateDivider(mutationList: MutationRecord[]): void {
  const mutation = mutationList.find(
    (mutation) => mutation.type === 'attributes'
  )

  if (mutation) {
    defaultValues.divider = (mutation.target as HTMLInputElement).value
  }
}

function setAction(
  value: string | null,
  type: null | keyof AvailableActionsMap,
  childActions: Record<string, Action> = {}
): void {
  if (value && type) {
    const currentAction = actions[type]

    if (isSeasonalityManualAction(currentAction)) {
      if (currentAction.showPercentage) {
        value = value.replace('%', '') + '%'
      }
    }
  }

  setPreviousAction(elementId.value, { type, value }, childActions)
}

function defaulValueForManualAction(manualAction: {
  name: ManualActionNames
}): string {
  return defaultValue.value[manualAction.name]
}
</script>

<style scoped lang="scss">
@import 'silverfin-ui-kit/src/scss/tooling';

.budget-actions {
  $select-chrevon-position: 22px;

  &__parent-element {
    position: relative;
    font-weight: normal;
  }

  &__sf-form-select-wrapper {
    margin: 5px 5px 5px 20px;
  }

  &__sf-form-select {
    padding: 2px 20px 3px 5px !important;
    @include font-size('body', 's');
  }

  &__sf-form-select-open {
    padding: 2px 20px 3px 5px !important;
    box-shadow: none !important;
    border-color: white !important;
    @include font-size('body', 's');
  }

  &__sf-form-select-wrapper::after {
    right: 8px;
  }

  &__tab-wrapper {
    margin: 0 -16px;
  }

  &__tab-link {
    margin: 0;
  }

  &__tab-text {
    padding: 6px;
  }

  &__select-text {
    padding: 0 11px 0 5px;
    @include font-weight('normal');

    user-select: none;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__select-open::before {
    content: '';
    position: absolute;
    height: 100%;
    width: calc(100% - $select-chrevon-position);
    top: 0;
    left: 0;
    border-radius: 8px 0 0 8px;
    @include background-color('snow', 'dark');

    z-index: 1;
    opacity: 0.5;
  }

  &__select-text::after {
    content: '';
    position: absolute;
    height: 100%;
    bottom: 0;
    right: $select-chrevon-position;
    border-right: 1px solid -color('snow', 'dark');
  }

  &__actions-card {
    border-radius: 8px;
    background-color: white;
    position: absolute;
    z-index: 3;
    padding: 16px;
    box-shadow: 0 4px 8px -color('snow', 'darker');
    right: 0;
    min-width: 230px;
  }

  &__actions-card-element {
    padding: 8px 0;
    position: relative;
  }

  &__actions-card-bordered-element::before {
    content: '';
    position: absolute;
    width: calc(100% + 32px);
    left: -16px;
    top: 0;
    border-top: 1px solid -color('snow', 'dark');
  }

  &__delete-data {
    padding: 16px 0 0;
    text-align: center;
  }

  &__budget-actions-error {
    margin: 0 0 12px;
    white-space: initial;
    word-wrap: break-word;
  }
}
</style>
